/**
 * Janko Piano - Input Module
 * Handles touch, mouse, and keyboard input
 * @version 0.2.63
 */

import { CONFIG, SECRET_CODE, INSTRUMENT_IDS } from './config.js';
import { activePointers, triggerNotes, stopNote, engageSustain, releaseSustain, getCurrentInstrument } from './audio.js';
import { getCanvas, getKey, keys, metrics, generateKeys, requestRender } from './renderer.js';
import { 
    getVisibleModal, 
    openInstrumentGrid, 
    closeInstrumentGrid, 
    closeSettings, 
    closeQuickSettings,
    openQuickSettings,
    toggleInfo,
    toggleSettings,
    setInst,
    cycleInst,
    updateModalSelection,
    getSelectedInstIndex,
    setSelectedInstIndex,
    findNextVisualElement,
    setScroll,
    showToast
} from './ui.js';

// =============================================================================
// SECRET CODE STATE
// =============================================================================

let inputBuffer = [];

function checkSecret(note) {
    inputBuffer.push(note);
    if (inputBuffer.length > SECRET_CODE.length) inputBuffer.shift();
    if (JSON.stringify(inputBuffer) === JSON.stringify(SECRET_CODE)) {
        const devTools = document.getElementById('dev-tools-wrapper');
        if (devTools) devTools.style.display = 'block';
        showToast("👨‍💻 Developer Mode Unlocked!");
        inputBuffer = [];
    }
}

// =============================================================================
// POINTER HANDLING
// =============================================================================

export function handlePointer(type, id, x, y, updateMinimapFn) {
    const k = getKey(x, y);
    const active = activePointers.get(id);
    let needsRender = false;
    
    if (type === 'up') {
        if (active) {
            stopNote(active);
            activePointers.delete(id);
            needsRender = true;
        }
    } else if ((type === 'down' || type === 'move')) {
        const same = k && active && active.some(i => i.keyId === k.id && i.isRoot);
        if (k) {
            if (!same) {
                if (active) stopNote(active);
                activePointers.set(id, triggerNotes(k.midi, id, keys));
                needsRender = true;
            }
            if (type === 'down') checkSecret(k.note);
        } else {
            if (active) {
                stopNote(active);
                activePointers.delete(id);
                needsRender = true;
            }
        }
    }
    
    if (needsRender) {
        requestRender(getCurrentInstrument());
    }
}

// =============================================================================
// KEYBOARD EVENT HANDLERS
// =============================================================================

export function setupKeyboardListeners(updateMinimapFn) {
    const canvas = getCanvas();
    
    window.addEventListener('keydown', e => {
        if (e.repeat && e.code !== 'ArrowLeft' && e.code !== 'ArrowRight') return;

        const activeModal = getVisibleModal();

        // Ctrl+Up/Down to cycle instruments (works anytime, no modal needed)
        if (e.ctrlKey && (e.code === 'ArrowUp' || e.code === 'ArrowDown')) {
            e.preventDefault();
            const delta = (e.code === 'ArrowDown') ? 1 : -1;
            cycleInst(delta, updateMinimapFn);
            return;
        }

        if (e.code.startsWith('Arrow')) {
            const isNavigatingModal = (activeModal === 'instrument' || activeModal === 'quick-settings' || activeModal === 'settings');

            if (!isNavigatingModal && window.QWERTY) {
                const slider = document.getElementById('scroll-slider');
                const maxScroll = slider ? parseFloat(slider.max || 100) : 100;
                const viewCols = (canvas && metrics && metrics.w > 0) ? (canvas.width / metrics.w) : 15;

                const handled = window.QWERTY.handleNavigation(
                    e.code,
                    CONFIG.scrollPos,
                    maxScroll,
                    viewCols,
                    CONFIG.rows,
                    (newScroll) => setScroll(newScroll, updateMinimapFn),
                    () => { generateKeys(updateMinimapFn); requestRender(getCurrentInstrument()); },
                    CONFIG.splitOctave
                );
                if (handled) {
                    e.preventDefault();
                    return;
                }
            }
        }

        // Modal Navigation
        if (activeModal === 'instrument') {
            if (e.code === 'ArrowLeft' || e.code === 'ArrowRight') {
                e.preventDefault();
                const delta = (e.code === 'ArrowRight') ? 1 : -1;
                let idx = getSelectedInstIndex();
                idx = (idx + delta + INSTRUMENT_IDS.length) % INSTRUMENT_IDS.length;
                setSelectedInstIndex(idx);
                updateModalSelection();
                return;
            }

            if (e.code === 'ArrowUp' || e.code === 'ArrowDown') {
                e.preventDefault();
                const currentEl = document.getElementById('instrument-btn-' + getSelectedInstIndex());
                if (!currentEl) {
                    setSelectedInstIndex(0);
                    updateModalSelection();
                    return;
                }

                const nextEl = findNextVisualElement('inst-grid-container', currentEl, e.code, '.instrument-btn');
                if (nextEl) {
                    const parts = nextEl.id.split('-');
                    const idx = parseInt(parts[parts.length - 1]);
                    if (!isNaN(idx)) {
                        setSelectedInstIndex(idx);
                        updateModalSelection();
                    }
                }
                return;
            }

            if (e.code === 'Enter') {
                e.preventDefault();
                setInst(INSTRUMENT_IDS[getSelectedInstIndex()], updateMinimapFn);
                closeInstrumentGrid();
                return;
            }
        } else if (activeModal === 'quick-settings') {
            if (e.code.startsWith('Arrow')) {
                e.preventDefault();
                let cur = document.activeElement;
                const modal = document.getElementById('quick-settings-modal');
                if (!modal.contains(cur) || cur.tagName !== 'BUTTON') {
                    cur = modal.querySelector('button');
                }

                const next = findNextVisualElement('quick-settings-modal', cur, e.code, 'button');
                if (next) next.focus();
                return;
            }
            if (e.code === 'Enter') {
                e.preventDefault();
                if (document.activeElement) document.activeElement.click();
                return;
            }
        } else if (activeModal === 'info') {
            if (e.code === 'ArrowDown') {
                document.querySelector('.modal-panel').scrollTop += 20;
                return;
            }
            if (e.code === 'ArrowUp') {
                document.querySelector('.modal-panel').scrollTop -= 20;
                return;
            }
            if (e.code === 'Enter') {
                e.preventDefault();
                openQuickSettings();
                return;
            }
        } else if (activeModal === 'settings') {
            if ((e.code === 'ArrowLeft' || e.code === 'ArrowRight') && document.activeElement.tagName === 'INPUT') {
                return;
            }

            if (e.code.startsWith('Arrow')) {
                e.preventDefault();
                let cur = document.activeElement;
                const modal = document.getElementById('settings-modal');
                if (!modal.contains(cur)) {
                    cur = modal.querySelector('input, button, select');
                }

                const next = findNextVisualElement('settings-modal', cur, e.code, 'button, input, select');
                if (next) next.focus();
                return;
            }

            if (e.code === 'Enter') {
                if (document.activeElement.tagName === 'BUTTON') {
                    e.preventDefault();
                    document.activeElement.click();
                } else if (document.activeElement.tagName === 'SELECT') {
                    // Default behavior
                } else {
                    e.preventDefault();
                    closeSettings();
                }
                return;
            }
        }

        // Toggle Logic
        if (e.code === 'Tab') {
            e.preventDefault();
            e.stopPropagation();
            if (activeModal === 'instrument') {
                closeInstrumentGrid();
            } else {
                closeSettings();
                closeQuickSettings();
                document.getElementById('info-modal').classList.remove('visible');
                openInstrumentGrid();
            }
            return;
        }
        
        if (e.code === 'Escape') {
            e.preventDefault();
            if (activeModal === 'instrument') closeInstrumentGrid();
            else if (activeModal === 'quick-settings') closeQuickSettings();
            else if (activeModal === 'info') toggleInfo();
            else toggleSettings();
            return;
        }

        if (e.code === 'Backquote') {
            e.preventDefault();
            if (activeModal === 'quick-settings') closeQuickSettings();
            else openQuickSettings();
            return;
        }

        // Allow Space (sustain) and QWERTY playing even when modals are open
        if (e.code === 'Space') {
            e.preventDefault();
            engageSustain();
            return;
        }

        // QWERTY key mapping - works even with modals open
        if (window.QWERTY) {
            const offset = window.QWERTY.getLayoutOffset(canvas, metrics.w, CONFIG.rows);
            const key = window.QWERTY.findPianoKey(e.code, offset, keys, metrics.w, CONFIG.scrollPos, CONFIG.splitOctave);

            if (key && !activePointers.has(e.code)) {
                activePointers.set(e.code, triggerNotes(key.midi, e.code, keys));
                checkSecret(key.note);
                requestRender(getCurrentInstrument());
            }
        }
    });

    window.addEventListener('keyup', e => {
        if (e.code === 'Space') {
            releaseSustain();
            return;
        }
        const d = activePointers.get(e.code);
        if (d) {
            stopNote(d);
            activePointers.delete(e.code);
            requestRender(getCurrentInstrument());
        }
    });
}

// =============================================================================
// MOUSE/TOUCH EVENT HANDLERS
// =============================================================================

export function setupPointerListeners(updateMinimapFn) {
    const canvas = getCanvas();
    
    canvas.addEventListener('mousedown', e => handlePointer('down', 'ms', e.offsetX, e.offsetY, updateMinimapFn));
    canvas.addEventListener('mousemove', e => {
        if (e.buttons) handlePointer('move', 'ms', e.offsetX, e.offsetY, updateMinimapFn);
    });
    window.addEventListener('mouseup', () => handlePointer('up', 'ms'));

    canvas.addEventListener('touchstart', e => {
        e.preventDefault();
        const r = canvas.getBoundingClientRect();
        for (let i = 0; i < e.changedTouches.length; i++) {
            const t = e.changedTouches[i];
            handlePointer('down', t.identifier, t.clientX - r.left, t.clientY - r.top, updateMinimapFn);
        }
    }, { passive: false });

    canvas.addEventListener('touchmove', e => {
        e.preventDefault();
        const r = canvas.getBoundingClientRect();
        for (let i = 0; i < e.changedTouches.length; i++) {
            const t = e.changedTouches[i];
            handlePointer('move', t.identifier, t.clientX - r.left, t.clientY - r.top, updateMinimapFn);
        }
    }, { passive: false });

    canvas.addEventListener('touchend', e => {
        e.preventDefault();
        for (let i = 0; i < e.changedTouches.length; i++) {
            handlePointer('up', e.changedTouches[i].identifier);
        }
    });

    // Block default zoom behaviors
    window.addEventListener('wheel', e => {
        if (e.ctrlKey) e.preventDefault();
    }, { passive: false });

    window.addEventListener('gesturestart', e => e.preventDefault());
    window.addEventListener('gesturechange', e => e.preventDefault());
    window.addEventListener('gestureend', e => e.preventDefault());

    canvas.addEventListener('wheel', e => {
        e.preventDefault();
        
        // Use horizontal scroll (deltaX) or vertical scroll (deltaY) for keyboard panning
        // Touchpad 2-finger pan typically uses deltaX, mouse wheel uses deltaY
        let delta = e.deltaX !== 0 ? e.deltaX : e.deltaY;
        
        // Normalize delta (different browsers/devices have different scales)
        // Shift+wheel can also be used for horizontal scroll
        if (e.shiftKey && e.deltaX === 0) {
            delta = e.deltaY;
        }
        
        // Convert pixel delta to scroll units (1 scroll unit = 1 octave = 6 columns)
        // Typical wheel delta is ~100 pixels per notch
        const scrollSensitivity = 0.005; // Adjust for smooth scrolling
        const scrollDelta = delta * scrollSensitivity;
        
        // Get current scroll bounds
        const slider = document.getElementById('scroll-slider');
        const maxScroll = slider ? parseFloat(slider.max) : 5.2;
        
        // Calculate new scroll position
        let newScroll = CONFIG.scrollPos + scrollDelta;
        if (newScroll < 0) newScroll = 0;
        if (newScroll > maxScroll) newScroll = maxScroll;
        
        // Apply scroll
        if (newScroll !== CONFIG.scrollPos) {
            setScroll(newScroll, updateMinimapFn);
        }
    }, { passive: false });
}
